import React, { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { 
  Film, 
  Eye, 
  ThumbsUp, 
  Loader2, 
  TrendingUp,
  Play,
  Clock,
  RefreshCw,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import * as api from '@/lib/api';
import { useLanguage } from '@/lib/LanguageContext';
import { useAuth } from '@/lib/AuthContext';
import { useNavigate } from 'react-router-dom';

const MetricCard = ({ title, value, icon: Icon, trend, color = 'primary', loading, trendLabel }) => {
  const numericValue = typeof value === 'number' ? value : parseFloat(value) || 0;
  const showTrend = trend !== undefined && numericValue > 0;
  
  return (
    <Card className="metric-card bg-card border-border hover:border-primary/30 transition-all duration-300">
      <CardContent className="p-6">
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-32" />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">{title}</p>
              <div className={`w-10 h-10 rounded-lg bg-${color}/15 flex items-center justify-center`}>
                <Icon className={`w-5 h-5 text-${color}`} strokeWidth={1.5} />
              </div>
            </div>
            <p className="text-3xl font-heading font-bold tracking-tight">
              {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
            {showTrend && (
              <div className="flex items-center gap-1 mt-2">
                <TrendingUp className="w-4 h-4 text-green-500" />
                <span className="text-sm text-green-500">+{trend}%</span>
                <span className="text-xs text-muted-foreground ml-1">{trendLabel}</span>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Custom Tooltip component for charts  
const DashboardTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-xl">
        <p className="text-sm font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={`${entry.name}-${index}`} className="text-xs" style={{ color: entry.color }}>
            {entry.name}: {entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const ProcessingJobItem = ({ job, getJobTypeLabel }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'processing': return 'text-secondary animate-pulse';
      case 'queued': return 'text-yellow-500';
      case 'completed': return 'text-green-500';
      case 'failed': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border border-border/50">
      <div className={`w-2 h-2 rounded-full ${job.status === 'processing' ? 'bg-secondary animate-pulse' : 'bg-muted-foreground'}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{getJobTypeLabel(job.job_type)}</p>
        <p className="text-xs text-muted-foreground truncate">ID: {job.target_id?.slice(0, 8)}...</p>
      </div>
      <div className="flex items-center gap-3">
        <div className="w-24">
          <Progress value={job.progress || 0} className="h-1.5" />
        </div>
        <Badge variant="outline" className={getStatusColor(job.status)}>
          {job.status}
        </Badge>
      </div>
    </div>
  );
};

const RecentClipItem = ({ clip }) => (
  <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/30 border border-border/50 hover:border-primary/30 transition-colors">
    <div className="w-20 h-12 rounded-lg bg-muted overflow-hidden flex-shrink-0">
      {clip.thumbnail_url ? (
        <img src={clip.thumbnail_url} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Play className="w-5 h-5 text-muted-foreground" />
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium truncate">{clip.title}</p>
      <p className="text-xs text-muted-foreground">{clip.duration_seconds}s • {clip.original_channel}</p>
    </div>
    <Badge 
      variant={clip.status === 'published' ? 'default' : 'outline'}
      className={clip.status === 'published' ? 'bg-green-500/20 text-green-500 border-green-500/30' : ''}
    >
      {clip.status}
    </Badge>
  </div>
);

export default function Dashboard() {
  const { t } = useLanguage();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentClips, setRecentClips] = useState([]);
  const [processingQueue, setProcessingQueue] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);

  const getJobTypeLabel = (type) => {
    const labels = {
      'full_pipeline': t('jobTypes.fullPipeline'),
      'viral_analysis': t('jobTypes.viralAnalysis'),
      'render_clip': t('jobTypes.renderClip'),
      'transcription': t('jobTypes.transcription'),
    };
    return labels[type] || type;
  };

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, clipsRes] = await Promise.all([
        api.getDashboardStats(),
        api.getClips(null, 5)
      ]);
      
      setStats(statsRes.data);
      setRecentClips(clipsRes.data.clips || []);
      
      // Generate chart data - show empty/zero if no real data
      const chart = [];
      const hasRealData = statsRes.data.has_real_data;
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        chart.push({
          date: date.toISOString().split('T')[0],
          views: hasRealData ? Math.floor((statsRes.data.total_views || 0) / 7) : 0,
          likes: hasRealData ? Math.floor((statsRes.data.total_likes || 0) / 7) : 0
        });
      }
      setChartData(chart);
    } catch (error) {
      logger.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      await api.seedDemoData();
      toast.success(t('dashboard.demoDataSeeded'));
      fetchData();
      await refreshUser();
    } catch (error) {
      toast.error('Failed to seed demo data');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="dashboard">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">{t('dashboard.welcomeBack')}</h1>
          <p className="text-muted-foreground">{t('dashboard.monitorPipeline')}</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={fetchData}
            disabled={loading}
            data-testid="refresh-dashboard-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('dashboard.refresh')}
          </Button>
          <Button 
            onClick={handleSeedData}
            disabled={seeding}
            className="glow-primary"
            data-testid="seed-data-btn"
          >
            {seeding ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            {t('dashboard.seedDemoData')}
          </Button>
        </div>
      </div>

      {/* Info Banner when no real data */}
      {!loading && stats && !stats.has_real_data && (
        <Card className="bg-blue-500/10 border-blue-500/30">
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-blue-500" />
              <div>
                <p className="font-medium text-blue-500">Nenhum vídeo publicado ainda</p>
                <p className="text-sm text-muted-foreground">
                  As métricas acima mostram apenas dados de vídeos realmente publicados nas plataformas. 
                  Publique seu primeiro clipe para começar a ver suas estatísticas reais!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title={t('dashboard.totalClips')}
          value={stats?.total_clips || 0}
          icon={Film}
          trend={12}
          trendLabel={t('dashboard.vsLastWeek')}
          loading={loading}
        />
        <MetricCard
          title={t('dashboard.totalViews')}
          value={stats?.total_views || 0}
          icon={Eye}
          trend={8}
          trendLabel={t('dashboard.vsLastWeek')}
          loading={loading}
        />
        <MetricCard
          title={t('dashboard.totalLikes')}
          value={stats?.total_likes || 0}
          icon={ThumbsUp}
          trend={15}
          trendLabel={t('dashboard.vsLastWeek')}
          loading={loading}
        />
        <MetricCard
          title={t('dashboard.engagementRate')}
          value={`${stats?.engagement_rate || 0}%`}
          icon={TrendingUp}
          loading={loading}
        />
      </div>

      {/* Charts and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Performance Chart */}
        <Card className="lg:col-span-2 bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading">{t('dashboard.performanceOverview')}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="date" 
                    stroke="hsl(var(--muted-foreground))" 
                    fontSize={12}
                    tickFormatter={(value) => value.slice(5)}
                  />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip content={<DashboardTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="views" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={false}
                    name="Views"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="likes" 
                    stroke="hsl(var(--secondary))" 
                    strokeWidth={2}
                    dot={false}
                    name="Likes"
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Processing Queue */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-heading">{t('dashboard.processingQueue')}</CardTitle>
              <Badge variant="outline" className="text-secondary">
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                {stats?.active_jobs || 0} {t('dashboard.active')}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
              </div>
            ) : processingQueue.length > 0 ? (
              <div className="space-y-3">
                {processingQueue.map((job) => (
                  <ProcessingJobItem key={job.id} job={job} getJobTypeLabel={getJobTypeLabel} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Clock className="w-10 h-10 text-muted-foreground mb-3" />
                <p className="text-sm text-muted-foreground">{t('dashboard.noActiveJobs')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Clips */}
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-heading">{t('dashboard.recentClips')}</CardTitle>
            <Badge variant="outline">
              {stats?.published_clips || 0} {t('dashboard.published')}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
            </div>
          ) : recentClips.length > 0 ? (
            <div className="space-y-3">
              {recentClips.map((clip) => (
                <RecentClipItem key={clip.id} clip={clip} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Film className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground mb-4">{t('dashboard.noClipsYet')}</p>
              <Button onClick={handleSeedData} disabled={seeding}>
                {t('dashboard.generateDemoClips')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
